class MobileDock extends HTMLElement {
  constructor() {
    super();

    this.lastScrollY = 0;
    this.scrollThreshold = 50;
    this.ticking = false;
    this.lastScrollForHeaderFix = 0;

    this.onScrollHandler = null;
    this.onResizeHandler = null;
  }

  connectedCallback() {
    const header = document.querySelector(
      '.shopify-section-header-group, .header-wrapper, #shopify-section-header'
    );

    this.isAction = false;

    this.scrollY = header
      ? Math.max(0, parseInt(header.getBoundingClientRect().bottom, 10) || 0)
      : 0;

    this.behavior = this.dataset.behavior || 'scroll_up';

    /*
     * The Liquid section initially renders with:
     * visibility:hidden; opacity:0;
     *
     * Remove those inline properties so the stylesheet
     * can control visibility using .is-active.
     */
    this.style.removeProperty('visibility');
    this.style.removeProperty('opacity');

    /*
     * ALWAYS MODE
     *
     * If "Always" is selected in the Theme Editor,
     * activate the dock immediately.
     */
    if (this.behavior === 'always') {
      this.classList.add('is-active');
    }

    /*
     * Always show inside Shopify Theme Editor.
     */
    if (
      typeof Shopify !== 'undefined' &&
      Shopify.designMode
    ) {
      this.classList.add('is-active');
    }

    /*
     * Dock action buttons.
     */
    this.querySelectorAll('.dock__item[data-action]').forEach((button) => {
      button.addEventListener(
        'click',
        this.onButtonClick.bind(this)
      );
    });

    /*
     * Drawer/modal events.
     */
    document.addEventListener(
      'menudrawer:close',
      this.hideStickyHeader.bind(this)
    );

    document.addEventListener(
      'searchmodal:close',
      this.hideStickyHeader.bind(this)
    );

    /*
     * Scroll handler.
     */
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener(
      'scroll',
      this.onScrollHandler,
      { passive: true }
    );

    /*
     * Resize handler.
     */
    this.onResizeHandler = () => {
      if (window.innerWidth < 750) {
        this.updateDockHeight();

        if (this.behavior === 'always') {
          this.classList.add('is-active');
        }
      }
    };

    window.addEventListener(
      'resize',
      this.onResizeHandler,
      { passive: true }
    );

    /*
     * Add body class used by the stylesheet.
     */
    document.body.classList.add('has-mobile-dock');

    /*
     * Initial height calculation.
     */
    this.updateDockHeight();

    /*
     * Initialize header behavior.
     */
    this.initializeStickyHeaderBehavior();

    /*
     * Run initial visibility calculation.
     */
    this.updateDockAndHeaderVisibility();

    /*
     * Recalculate after initial browser layout.
     */
    requestAnimationFrame(() => {
      this.updateDockHeight();

      if (this.behavior === 'always') {
        this.classList.add('is-active');
      }
    });
  }

  disconnectedCallback() {
    if (this.onScrollHandler) {
      window.removeEventListener(
        'scroll',
        this.onScrollHandler
      );
    }

    if (this.onResizeHandler) {
      window.removeEventListener(
        'resize',
        this.onResizeHandler
      );
    }
  }

  updateDockHeight() {
    if (window.innerWidth >= 750) return;

    const height = this.offsetHeight;

    if (height > 0) {
      document.documentElement.style.setProperty(
        '--mobile-dock-height',
        `${height}px`
      );
    }
  }

  initializeStickyHeaderBehavior() {
    /*
     * IMPORTANT:
     *
     * The mobile dock and sticky header are now independent.
     * We do NOT hide the dock based on header visibility.
     */

    if (this.behavior === 'always') {
      this.classList.add('is-active');
    }
  }

  onScroll() {
    if (this.ticking) return;

    window.requestAnimationFrame(() => {
      this.updateDockAndHeaderVisibility();
      this.ticking = false;
    });

    this.ticking = true;
  }

  updateDockAndHeaderVisibility() {
    /*
     * Desktop is handled by CSS.
     */
    if (window.innerWidth >= 750) {
      return;
    }

    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;

    /*
     * ==========================================================
     * ALWAYS MODE
     * ==========================================================
     *
     * Once Always is selected, scrolling cannot remove
     * .is-active.
     */
    if (this.behavior === 'always') {
      this.classList.add('is-active');

      this.lastScrollY = scrollTop;

      return;
    }

    /*
     * ==========================================================
     * SCROLL UP MODE
     * ==========================================================
     */

    if (scrollTop < this.scrollY) {
      /*
       * Near the top of the page.
       */
      this.classList.remove('is-active');
    } else {
      const isScrollingUp =
        scrollTop < this.lastScrollY;

      const isScrollingDown =
        scrollTop >
        this.lastScrollY +
        this.scrollThreshold;

      if (isScrollingUp) {
        this.classList.add('is-active');
      } else if (isScrollingDown) {
        this.classList.remove('is-active');
      }
    }

    this.lastScrollY = scrollTop;
  }

  onButtonClick(event) {
    event.preventDefault();

    const target = event.currentTarget;

    switch (target.dataset.action) {

      /*
       * ========================================================
       * CART
       * ========================================================
       */

      case 'cart': {
        this.isAction = true;

        const miniCart =
          document.querySelector('mini-cart');

        if (
          miniCart &&
          typeof miniCart.open === 'function'
        ) {
          miniCart.open();

          if (document.activeElement) {
            document.activeElement.blur();
          }

          break;
        }

        const cartDrawer =
          document.querySelector('cart-drawer');

        if (
          cartDrawer &&
          typeof cartDrawer.open === 'function'
        ) {
          cartDrawer.open();

          if (document.activeElement) {
            document.activeElement.blur();
          }

          break;
        }

        window.location.href =
          window.Shopify?.routes?.cart ||
          '/cart';

        break;
      }


      /*
       * ========================================================
       * MENU
       * ========================================================
       */

      case 'menu': {
        event.stopPropagation();

        this.isAction = true;

        this.ensureHeaderVisibility(() => {
          const headerDrawer =
            document.querySelector('header-drawer');

          if (headerDrawer) {
            const summary =
              headerDrawer.querySelector('summary');

            if (summary) {
              summary.click();
            }
          }
        });

        break;
      }


      /*
       * ========================================================
       * SEARCH
       * ========================================================
       */

      case 'search': {
        event.stopPropagation();

        this.isAction = true;

        this.ensureHeaderVisibility(() => {
          const searchModals =
            document.querySelectorAll('search-modal');

          let openedSearch = false;

          if (searchModals.length > 0) {
            searchModals.forEach((searchModal) => {
              const style =
                window.getComputedStyle(searchModal);

              if (style.display === 'none') {
                return;
              }

              if (
                typeof searchModal.open === 'function'
              ) {
                searchModal.open();

                openedSearch = true;

                const input =
                  searchModal.querySelector(
                    'input:not([type="hidden"])'
                  );

                if (input) {
                  input.focus();
                }
              }
            });
          }

          if (openedSearch) {
            return;
          }

          /*
           * Dawn-style search fallback.
           */
          const searchModal =
            document.querySelector(
              'details-modal#header-search'
            );

          if (searchModal) {
            const details =
              searchModal.querySelector('details');

            if (details) {
              details.setAttribute('open', '');

              const input =
                details.querySelector(
                  'input:not([type="hidden"])'
                );

              if (input) {
                setTimeout(() => {
                  input.focus();
                }, 100);
              }

              return;
            }
          }

          /*
           * Final fallback.
           */
          window.location.href =
            window.Shopify?.routes?.search ||
            '/search';
        });

        break;
      }
    }
  }

  ensureHeaderVisibility(callback) {
    const header = document.querySelector(
      '.shopify-section-group-header-group[id*="__header"], #shopify-section-header, .header-wrapper'
    );

    if (!header) {
      callback();
      return;
    }

    const rect =
      header.getBoundingClientRect();

    const isHeaderVisible =
      rect.bottom > 0 &&
      rect.top < window.innerHeight;

    /*
     * Header already visible.
     */
    if (isHeaderVisible) {
      callback();
      return;
    }

    /*
     * Save original header styles.
     */
    const originalPosition =
      header.style.position;

    const originalTop =
      header.style.top;

    const originalLeft =
      header.style.left;

    const originalWidth =
      header.style.width;

    const originalZIndex =
      header.style.zIndex;

    const originalTransition =
      header.style.transition;

    const originalTransform =
      header.style.transform;

    /*
     * Temporarily display header so menu/search
     * can open correctly.
     */
    header.style.position = 'fixed';
    header.style.top = '0';
    header.style.left = '0';
    header.style.width = '100%';
    header.style.zIndex = '1000';
    header.style.transform = 'translateY(0)';

    header.setAttribute(
      'data-mobile-dock-fixed',
      'true'
    );

    const restoreHeader = () => {
      let restored = false;

      header.style.transition =
        'transform 0.3s ease-out';

      header.style.transform =
        'translateY(-100%)';

      const onTransitionDone = () => {
        if (restored) return;

        restored = true;

        header.style.position =
          originalPosition;

        header.style.top =
          originalTop;

        header.style.left =
          originalLeft;

        header.style.width =
          originalWidth;

        header.style.zIndex =
          originalZIndex;

        header.style.transition =
          originalTransition;

        header.style.transform =
          originalTransform;

        header.removeAttribute(
          'data-mobile-dock-fixed'
        );

        header.removeEventListener(
          'transitionend',
          onTransitionDone
        );
      };

      header.addEventListener(
        'transitionend',
        onTransitionDone,
        { once: true }
      );

      /*
       * Safety fallback.
       */
      setTimeout(
        onTransitionDone,
        350
      );
    };

    setTimeout(() => {
      callback();

      /*
       * Watch the menu/search details element.
       */
      const openDetails =
        header.querySelector(
          'details[open]'
        );

      if (openDetails) {
        const observer =
          new MutationObserver(
            (mutations) => {
              for (const mutation of mutations) {
                if (
                  mutation.attributeName === 'open' &&
                  !openDetails.hasAttribute('open')
                ) {
                  restoreHeader();
                  observer.disconnect();

                  return;
                }
              }
            }
          );

        observer.observe(
          openDetails,
          {
            attributes: true,
            attributeFilter: ['open']
          }
        );

        return;
      }

      /*
       * Fallback restoration on next scroll.
       */
      const hideOnScroll = () => {
        if (
          Math.abs(
            window.pageYOffset -
            this.lastScrollForHeaderFix
          ) > 10
        ) {
          restoreHeader();

          window.removeEventListener(
            'scroll',
            hideOnScroll
          );
        }
      };

      this.lastScrollForHeaderFix =
        window.pageYOffset ||
        document.documentElement.scrollTop;

      window.addEventListener(
        'scroll',
        hideOnScroll,
        { passive: true }
      );
    }, 50);
  }

  hideStickyHeader() {
    const header =
      document.querySelector('sticky-header');

    if (!header) return;

    const isSmallScreen =
      window.innerWidth < 750;

    if (
      isSmallScreen &&
      this.isAction &&
      typeof header.hide === 'function'
    ) {
      setTimeout(() => {
        header.hide();

        this.isAction = false;
      }, 500);
    }
  }
}


/*
 * Prevent duplicate custom-element registration.
 */
if (!customElements.get('mobile-dock')) {
  customElements.define(
    'mobile-dock',
    MobileDock
  );
}